import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { syncInitGuard } from './core/guards/sync-init.guard';
import { tagLinkLandingGuard, tagLinkOpenGuard } from './core/guards/tag-link.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { organizationResolver } from './core/resolvers/organization.resolver';
import { userResolver } from './core/resolvers/user.resolver';
import { AcceptInvitationComponent } from './pages/accept-invitation/accept-invitation.component';
import { AdminSubscriptionsComponent } from './pages/admin/subscriptions/admin-subscriptions.component';
import { CheckPointFormComponent } from './pages/asset-form/checkPoint-form.component';
import { AuthCallbackComponent } from './pages/auth-callback/auth-callback.component';
import { BillingSuspendedComponent } from './pages/billing-suspended/billing-suspended.component';
import { BrowserErrorComponent } from './pages/browser-error/browser-error.component';
import { CheckPointsComponent } from './pages/checkpoints/checkPoints.component';
import { ChooseFormToSubmit as ChooseCheckPointForm } from './pages/choose-form-to-submit/choose-form-to-submit.component';
import { ChooseInstanceComponent } from './pages/choose-instance/choose-instance.component';
import { DbTestComponent } from './pages/db-test/db-test.component';
import { DevSqlComponent } from './pages/dev-sql/dev-sql.component';
import { DiagnosticsComponent } from './pages/diagnostics/diagnostics.component';
import { ErrorComponent } from './pages/error/error.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { FormSubmissionHistoryComponent } from './pages/form-submission-history/form-submission-history.component';
import { FormViewComponent } from './pages/form-view/form-view.component';
import { FormFormComponent } from './pages/formly-form/form-form.component';
import { FormsComponent } from './pages/forms/forms.component';
import { HomeComponent } from './pages/home/home.component';
import { LabelPrintingComponent } from './pages/label-printing/label-printing.component';
import { WriteNfcTagsComponent } from './pages/nfc-tags/write-nfc-tags.component';
import { LandingComponent } from './pages/landing/landing.component';
import { PrivacyPolicyComponent } from './pages/legal/privacy-policy.component';
import { TermsOfServiceComponent } from './pages/legal/terms-of-service.component';
import { LoggingComponent } from './pages/logging/logging.component';
import { LoginComponent } from './pages/login/login.component';
import { LookupListEditComponent } from './pages/lookup-lists/lookup-list-edit.component';
import { LookupListsComponent } from './pages/lookup-lists/lookup-lists.component';
import { NetworkActivityComponent } from './pages/network-activity/network-activity.component';
import { OnboardingComponent } from './pages/onboarding/onboarding.component';
import { OrganizationsComponent } from './pages/organizations/organizations.component';
import { PricingComponent } from './pages/pricing/pricing.component';
import { QrCodeComponent } from './pages/qr-code/qr-code.component';
import { ReportBuilderComponent } from './pages/reports/report-builder.component';
import { ReportResultsComponent } from './pages/reports/report-results.component';
import { ReportsHomeComponent } from './pages/reports/reports-home.component';
import { ScanComponent } from './pages/scan/scan.component';
import { TagLinkComponent } from './pages/tag-link/tag-link.component';
import { BillingSettingsComponent } from './pages/settings/billing/billing-settings.component';
import { InvitationsListComponent } from './pages/settings/invitations/invitations-list.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { SignupCompleteComponent } from './pages/signup-complete/signup-complete.component';
import { SignupComponent } from './pages/signup/signup.component';
import { SubmissionSuccessComponent } from './pages/submission-success/submission-success.component';
import { SubmitFormComponent } from './pages/submit-form-form/submit-form.component';
import { SyncErrorComponent } from './pages/sync-error/sync-error.component';
import { TeamComponent } from './pages/team/team.component';
import { ViewSubmissionComponent } from './pages/view-submission/view-submission.component';

export const routes: Routes = [
  // Public routes (no auth required)
  { path: 'landing', component: LandingComponent },
  { path: 'pricing', component: PricingComponent },
  { path: 'login', component: LoginComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'oauth-callback', component: AuthCallbackComponent },
  { path: 'browser-error', component: BrowserErrorComponent },
  { path: 'accept-invitation', component: AcceptInvitationComponent },
  { path: 'sync-error', component: SyncErrorComponent },
  { path: 'error', component: ErrorComponent },
  { path: 'terms', component: TermsOfServiceComponent },
  { path: 'privacy', component: PrivacyPolicyComponent },

  // Diagnostic routes (no auth required - for debugging database issues)
  { path: 'db-test', component: DbTestComponent },

  // Onboarding (auth guard + user resolver - user is completing signup with JWT but no org yet)
  {
    path: 'onboarding',
    component: OnboardingComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver },
  },

  // Billing lockout (auth guard only - no syncInitGuard/resolvers, since a
  // suspended org's WS connection and most REST calls are themselves blocked)
  {
    path: 'billing-suspended',
    component: BillingSuspendedComponent,
    canActivate: [authGuard],
  },

  // Stripe Checkout success_url/cancel_url target from the signup wizard's
  // paid-plan handoff (see signup.component.ts) - org already exists by now
  {
    path: 'signup-complete',
    component: SignupCompleteComponent,
    canActivate: [authGuard],
  },

  // Organization selection (no auth guard - user has valid JWT but needs to select/switch org)
  {
    path: 'organizations',
    component: OrganizationsComponent,
    data: { breadcrumb: { label: 'Organizations', parent: '/' } },
  },

  // Protected routes (syncInitGuard ensures sync is initialized before accessing data)
  // User and organization data loaded via resolvers before route activation
  {
    path: 'team',
    component: TeamComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Team', parent: '/' } },
  },
  {
    path: 'forbidden',
    component: ForbiddenComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Home', parent: null } },
  },

  // Asset routes - require permissions
  {
    path: 'checkpoints',
    component: CheckPointsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Checkpoints', parent: '/' } },
  },
  {
    path: 'checkpoints/new',
    component: CheckPointFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'New Checkpoint', parent: '/checkpoints' } },
  },
  {
    path: 'checkpoints/:id',
    component: CheckPointFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/checkpoints' } },
  },
  {
    path: 'submission/:checkpointId/:formId/history',
    component: FormSubmissionHistoryComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: { label: 'History', parent: '/submission/:checkpointId/:formId' },
    },
  },
  {
    path: 'submission/:checkpointId/:formId/history/:recordId',
    component: ViewSubmissionComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: { label: 'View', parent: '/submission/:checkpointId/:formId/history' },
    },
  },
  {
    path: 'checkpoints/:checkpointId/:formId/qr',
    component: QrCodeComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/checkpoints/:checkpointId' } },
  },
  {
    path: 'checkpoints/:checkpointId/:formId/labels',
    component: LabelPrintingComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['label.print'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: { label: 'Print Labels', parent: '/checkpoints/:checkpointId/:formId/view' },
    },
  },
  {
    path: 'checkpoints/:checkpointId/:formId/nfc',
    component: WriteNfcTagsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['label.write-nfc'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: { label: 'Write NFC Tags', parent: '/checkpoints/:checkpointId/:formId/view' },
    },
  },
  {
    path: 'checkpoints/:checkpointId/:formId/view',
    component: FormViewComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'View', parent: '/checkpoints/:checkpointId' } },
  },
  {
    path: 'checkpoints/:checkpointId/:formId/:instanceId/view',
    component: FormViewComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'View', parent: '/checkpoints/:checkpointId' } },
  },
  {
    path: 'checkpoints/:checkpointId/qr',
    component: QrCodeComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['asset.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/checkpoints/:checkpointId' } },
  },

  // Interaction Type (Form) routes - require permissions
  {
    path: 'forms',
    component: FormsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['form.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Forms', parent: '/' } },
  },

  // Lookup List routes - require permissions
  {
    path: 'lookup-lists',
    component: LookupListsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['lookup-list.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Lookup Lists', parent: '/' } },
  },
  {
    path: 'lookup-lists/new',
    component: LookupListEditComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['lookup-list.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'New Lookup List', parent: '/lookup-lists' } },
  },
  {
    path: 'lookup-lists/:id',
    component: LookupListEditComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['lookup-list.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/lookup-lists' } },
  },
  {
    path: 'forms/new',
    component: FormFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['form.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'New Form', parent: '/forms' } },
  },
  {
    path: 'forms/:id',
    component: FormFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['form.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/forms' } },
  },
  {
    path: 'forms/:id/:tab',
    component: FormFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['form.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/forms' } },
  },

  // Reporting routes - require permissions
  {
    path: 'reports',
    component: ReportsHomeComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['reports.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Reports', parent: '/' } },
  },
  {
    path: 'reports/new',
    component: ReportBuilderComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['reports.create'])],
    canDeactivate: [unsavedChangesGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'New Report', parent: '/reports' } },
  },
  {
    // Ad-hoc run of the current in-memory report definition (Phase 1).
    path: 'reports/run',
    component: ReportResultsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['reports.view'])],
    canDeactivate: [unsavedChangesGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Results', parent: '/reports' } },
  },
  {
    path: 'reports/:id/results',
    component: ReportResultsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['reports.view'])],
    canDeactivate: [unsavedChangesGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Results', parent: '/reports' } },
  },
  {
    path: 'reports/:id',
    component: ReportBuilderComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['reports.view'])],
    canDeactivate: [unsavedChangesGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/reports' } },
  },

  // Interaction recording routes - require permissions
  {
    path: 'submission',
    redirectTo: '/', // never reached directly; breadcrumb label only
    pathMatch: 'full',
    data: { breadcrumb: { label: 'Submission', parent: '/' } },
  },
  {
    path: 'submission/:checkpointId',
    component: ChooseCheckPointForm,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/submission' } },
  },
  {
    path: 'submission/:checkpointId/:formId',
    component: SubmitFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/submission/:checkpointId' } },
  },
  {
    path: 'submission/:checkpointId/:formId/choose-instance',
    component: ChooseInstanceComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/submission/:checkpointId' } },
  },
  {
    path: 'submission/:checkpointId/:formId/done',
    component: SubmissionSuccessComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Done', parent: '/submission/:checkpointId/:formId' } },
  },
  {
    path: 'submission/:checkpointId/:formId/:instanceId/done',
    component: SubmissionSuccessComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: { label: 'Done', parent: '/submission/:checkpointId/:formId/:instanceId' },
    },
  },
  {
    path: 'submission/:checkpointId/:formId/:instanceId/history',
    component: FormSubmissionHistoryComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: { label: 'History', parent: '/submission/:checkpointId/:formId/:instanceId' },
    },
  },
  {
    path: 'submission/:checkpointId/:formId/:instanceId/history/:recordId',
    component: ViewSubmissionComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.read'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: {
      breadcrumb: {
        label: 'View',
        parent: '/submission/:checkpointId/:formId/:instanceId/history',
      },
    },
  },
  {
    path: 'submission/:checkpointId/:formId/:instanceId',
    component: SubmitFormComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['submission.create'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: ':dynamic', parent: '/submission/:checkpointId' } },
  },
  // Backward compatibility for existing QR codes — already-printed QR codes use /record/:checkpointId or /record/:checkpointId/:formId
  { path: 'record/:checkpointId', redirectTo: 'submission/:checkpointId', pathMatch: 'full' },
  {
    path: 'record/:checkpointId/:formId',
    redirectTo: 'submission/:checkpointId/:formId',
    pathMatch: 'full',
  },
  {
    path: 'record/:checkpointId/:formId/:instanceId',
    redirectTo: 'submission/:checkpointId/:formId/:instanceId',
    pathMatch: 'full',
  },
  // Backward compat for routes created in Steps 2 & 4 (may exist in browser history / QR codes)
  { path: 'scan/:checkpointId', redirectTo: 'submission/:checkpointId', pathMatch: 'full' },
  {
    path: 'checkpoints/:checkpointId/submit/:formId',
    redirectTo: 'submission/:checkpointId/:formId',
    pathMatch: 'full',
  },
  {
    path: 'checkpoints/:checkpointId/submit/:formId/success',
    redirectTo: 'submission/:checkpointId/:formId/done',
    pathMatch: 'full',
  },
  {
    path: 'checkpoints/:checkpointId/submissions/:formId/history',
    redirectTo: 'submission/:checkpointId/:formId/history',
    pathMatch: 'full',
  },
  {
    path: 'checkpoints/:checkpointId/submissions/:formId/qr',
    redirectTo: 'checkpoints/:checkpointId/:formId/qr',
    pathMatch: 'full',
  },

  // NFC tag links (https://<host>/t/:code) opened outside the scan page — see tag-link.guard.ts
  { path: 't/:code', component: TagLinkComponent, canActivate: [tagLinkLandingGuard] },
  {
    path: 't/:code/open',
    canActivate: [authGuard, syncInitGuard, tagLinkOpenGuard],
    children: [],
  },

  // General routes - no specific permissions
  {
    path: 'scan',
    component: ScanComponent,
    canActivate: [authGuard, syncInitGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Scan Code', parent: '/' } },
  },

  // Super-admin-only QA tool (cross-org subscription management)
  {
    path: 'admin/subscriptions',
    component: AdminSubscriptionsComponent,
    canActivate: [authGuard, syncInitGuard, superAdminGuard],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Admin: Subscriptions', parent: '/' } },
  },

  // Settings routes - require permissions
  {
    path: 'settings/billing',
    component: BillingSettingsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.billing'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Billing', parent: '/settings' } },
  },
  {
    path: 'settings/invitations',
    component: InvitationsListComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['user.invite'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Invitations', parent: '/settings' } },
  },
  {
    path: 'settings/network-activity',
    component: NetworkActivityComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.settings'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Network Activity', parent: '/settings' } },
  },
  {
    path: 'settings/logging',
    component: LoggingComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.settings'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Logging', parent: '/settings' } },
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['org.settings'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Settings', parent: '/' } },
  },

  // Diagnostics - requires diagnostics.view permission
  {
    path: 'diagnostics',
    component: DiagnosticsComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['diagnostics.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'Diagnostics', parent: '/' } },
  },

  // SQL Console - requires sql.view permission (org-owner only)
  {
    path: 'dev/sql',
    component: DevSqlComponent,
    canActivate: [authGuard, syncInitGuard, permissionGuard(['sql.view'])],
    resolve: { user: userResolver, org: organizationResolver },
    data: { breadcrumb: { label: 'SQL Console', parent: '/' } },
  },

  { path: '**', redirectTo: '' },
];
